<template>
  <div class="tinymce-wrapper">
    <Editor
      v-model="localValue"
      :init="tinyInit"
      :api-key="apiKey"
      @input="onChange"
    />
  </div>
</template>

<script>
import {mapState} from 'vuex'
import {toJSON} from '@/utils/helpers'
import Editor from '@tinymce/tinymce-vue'

export default {
  name: 'RichHtmlPreserver',
  components: { Editor },
  props: {
    value: {
      type: String,
      default: ''
    },

    // Настраиваемые разделители (по умолчанию — {{ ... }})
    variableDelimiters: {
      type: Object,
      default: () => ({ open: '{{', close: '}}' })
    }
  },
  computed: {
    ...mapState('projects', {
      project: state => state.project
    }),
    variables () {
      const gv = toJSON(this.project.variables)

      if (Array.isArray(gv?.variables)) {
        return gv?.variables
      }

      const object = gv?.variables || {}
      return Object.entries(object).map(([key, value]) => ({
        ...value,
        hash: key
      }))
    },
    variablesText () {
      return this.variables.filter(variable => variable.type === 'text')
    },
    variablesLink () {
      return this.variables.filter(variable => variable.type === 'link')
    }
  },

  data () {
    const vm = this

    return {
      apiKey: 'dpzxkdu3i1v9uzz2f3tklz5u1qcjcm4uivmq8oa4sb59pf1d',
      localValue: this.value,
      tinyInit: {
        // базовая инициализация
        menubar: true,
        // добавили кнопку "variables" в тулбар
        toolbar: 'undo redo | bold italic strikethrough underline | superscript subscript | alignleft aligncenter alignright alignjustify | variables variables-link | removeformat',
        plugins: 'paste',
        branding: false,
        statusbar: true,
        height: 480,

        // максимально разрешаем всё
        forced_root_block: false,
        valid_elements: '*[*]',
        valid_children: '+*[*]',
        paste_data_images: true,
        paste_merge_formats: false,
        paste_tab_spaces: 0,
        paste_enable_default_filters: false,
        paste_remove_styles_if_webkit: false,
        paste_webkit_styles: 'all',
        paste_retain_style_properties: 'all',
        paste_convert_word_fake_lists: false,
        extended_valid_elements: 'p[*],div[*],span[*],span-var[*],h1[*],h2[*],h3[*],h4[*],h5[*],h6[*],table[*],thead[*],tbody[*],tr[*],td[*],th[*],ul[*],ol[*],li[*],blockquote[*],pre[*],a[*],img[*],sup[*],sub[*],strong[*],em[*],u[*],s[*]',
        convert_urls: false,
        remove_trailing_brs: false,

        // Кастомизация UI
        setup (editor) {
          // Универсальная вставка маркера
          const insertToken = (key, name) => {
            const selText = editor.selection.getContent({ format: 'text' }).trim()
            const tokenName = name || selText || 'variable'
            const open = (vm.variableDelimiters && vm.variableDelimiters.open) || '{{'
            const close = (vm.variableDelimiters && vm.variableDelimiters.close) || '}}'
            const token = `${open} ${tokenName} ${close}`

            // Insert as HTML wrapped in a span with data-key
            const spanHtml = `<span-var data-gvar="${key}">${token}</span-var>`
            editor.insertContent(spanHtml)
          }
          const insertLink = (key, url) => {
            const selText = editor.selection.getContent({ format: 'text' }).trim()
            const tokenName = selText || url || 'link'
            const open = '[['
            const close = ']]'
            const token = `${open} ${tokenName} ${close}`

            const safeUrl = '#' + key
            const spanHtml = `<a href="${safeUrl}" target="_blank" data-glink="${key}">${token}</a>`
            editor.insertContent(spanHtml)
          }

          // Диалог для произвольного имени переменной
          // const openCustomVarDialog = () => {
          //   editor.windowManager.open({
          //     title: 'Insert variable',
          //     body: {
          //       type: 'panel',
          //       items: [
          //         {
          //           type: 'input',
          //           name: 'variable',
          //           label: 'Variable name'
          //         }
          //       ]
          //     },
          //     buttons: [
          //       {
          //         type: 'cancel',
          //         text: 'Cancel'
          //       },
          //       {
          //         type: 'submit',
          //         text: 'Insert',
          //         primary: true
          //       }
          //     ],
          //     onSubmit: (api) => {
          //       const data = api.getData()
          //       const name = (data.variable || '').trim()
          //       if (name) insertToken(name)
          //       api.close()
          //     }
          //   })
          // }

          // Кнопка с выпадающим меню в тулбаре
          editor.ui.registry.addMenuButton('variables', {
            text: 'GV Text',
            tooltip: 'Insert variable',
            fetch: (callback) => {
              const items = (vm.variablesText || []).map(item => ({
                type: 'menuitem',
                text: item.name,
                onAction: () => insertToken(item.key, item.name)
              }))
              // Опция для произвольного имени
              // items.push({
              //   type: 'menuitem',
              //   text: 'Add new…',
              //   onAction: openCustomVarDialog
              // })
              callback(items)
            }
          })

          editor.ui.registry.addMenuButton('variables-link', {
            text: 'GV Link',
            tooltip: 'Insert link',
            fetch: (callback) => {
              const items = (vm.variablesLink || []).map(item => ({
                type: 'menuitem',
                text: item.name,
                onAction: () => insertLink(item.key, item.value)
              }))
              callback(items)
            }
          })

          // Команда, если захочешь дергать извне: editor.execCommand('insertVariable', false, 'username')
          editor.addCommand('insertVariable', (ui, value) => {
            insertToken(typeof value === 'string' && value ? value : null)
          })

          editor.addCommand('insertLink', (ui, value) => {
            insertLink(typeof value === 'string' && value ? value : null)
          })
        }
      }
    }
  },

  watch: {
    value (v) {
      if (v !== this.localValue) this.localValue = v
    }
  },

  methods: {
    onChange (newContent) {
      this.$emit('input', newContent)
      this.$emit('change', newContent)
    }
  }
}
</script>

<style>
.tinymce-wrapper {
  width: 100%;
}
</style>
